import { useState, useEffect, useCallback } from 'react';
import type {
  RenewalScheduleItem,
  RenewalScheduleSummary,
  RenewalScheduleResponse,
} from '@techgitter/contracts';

// Module-level caches keyed by customerId (persist across component mounts).
// `cachedSchedules` holds the flattened per-line-item view; `cachedSummaries`
// holds the schedule-level view (includes paused / itemless schedules).
const cachedSchedules = new Map<string, RenewalScheduleItem[]>();
const cachedSummaries = new Map<string, RenewalScheduleSummary[]>();

export function useRenewalSchedule(customerId: string | null) {
  const [schedule, setSchedule] = useState<RenewalScheduleItem[]>(
    customerId != null ? (cachedSchedules.get(customerId) ?? []) : []
  );
  const [schedules, setSchedules] = useState<RenewalScheduleSummary[]>(
    customerId != null ? (cachedSummaries.get(customerId) ?? []) : []
  );
  const [isLoading, setIsLoading] = useState(
    customerId != null && !cachedSchedules.has(customerId)
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (customerId == null) {
      setSchedule([]);
      setSchedules([]);
      setIsLoading(false);
      return;
    }

    if (cachedSchedules.has(customerId)) {
      setSchedule(cachedSchedules.get(customerId)!);
      setSchedules(cachedSummaries.get(customerId) ?? []);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/customers/${customerId}/renewal-schedule`, { signal });
      if (!response.ok) throw new Error(`Failed to load renewal schedule: ${response.status}`);

      const data: RenewalScheduleResponse = await response.json();
      cachedSchedules.set(customerId, data.items);
      cachedSummaries.set(customerId, data.schedules ?? []);
      setSchedule(data.items);
      setSchedules(data.schedules ?? []);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to load renewal schedule');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const retry = useCallback(() => {
    if (customerId != null) {
      cachedSchedules.delete(customerId);
      cachedSummaries.delete(customerId);
    }
    load();
  }, [customerId, load]);

  return { schedule, schedules, isLoading, error, retry };
}

/**
 * Synchronous lookup of cached renewal schedule for a customer.
 * Returns empty array if cache not yet populated.
 * Intended for use in pricing rules.
 */
export function getRenewalSchedule(customerId: string | number | undefined): RenewalScheduleItem[] {
  if (customerId == null) return [];
  return cachedSchedules.get(String(customerId)) ?? [];
}
